import { EventCard } from "../components/Event/EventCard";
import styles from "./EventList.module.scss";
import { useContext, useEffect, useState } from "react";
import { EventContext } from "../context/EventContext";
import {
  Button,
  Chip,
  CircularProgress,
  Pagination,
  PaginationItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { AuthContext } from "../context/AuthContext";
import { useEnumStore } from "../store/enumStore";
import { useTranslation } from "../i18n";
import { getEventTypeLabel } from "../utils/enumHelpers";

interface Filter {
  id: string;
  label: string;
}

export const EventList = () => {
  const { pathname } = useLocation();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const eventTypes = useEnumStore((state) => state.eventTypes);
  const eventContext = useContext(EventContext);
  const events = eventContext?.events;
  const volunteersEvents = eventContext?.volunteersEvents;
  const eventsLoading = eventContext?.eventsLoading ?? false;
  const volunteersEventsLoading =
    eventContext?.volunteersEventsLoading ?? false;
  const eventsError = eventContext?.eventsError ?? null;
  const volunteersEventsError = eventContext?.volunteersEventsError ?? null;
  const fetchEvents = eventContext?.fetchEvents ?? (() => {});
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<string>("all");

  let defaultFilters: Filter[] = [
    {
      id: "all",
      label: t("events.filterAll"),
    },
    {
      id: "is_starting_soon",
      label: t("events.filterSoon"),
    },
  ];

  if (eventTypes && eventTypes?.length > 0) {
    defaultFilters.push(
      ...eventTypes?.map((filter) => ({
        id: filter.event_type || filter.id.toString(),
        label: getEventTypeLabel(filter),
      })),
    );
  }

  const isVolunteer =
    currentUser?.userType === "volunteer" || pathname?.includes("volunteer");
  const isMyFavourites = pathname?.includes("favorite");
  const eventsResolved = !eventsLoading && events !== null;
  const volunteersEventsResolved =
    !volunteersEventsLoading && volunteersEvents !== null;
  const noDataOrg =
    !isVolunteer &&
    activeFilter === "all" &&
    eventsResolved &&
    (!events?.data || !Array.isArray(events.data) || events.data.length === 0);
  const noFilteredDataOrg =
    !isVolunteer &&
    activeFilter !== "all" &&
    eventsResolved &&
    (!events?.data || !Array.isArray(events.data) || events.data.length === 0);
  const noDataVolunteer =
    isVolunteer &&
    eventsResolved &&
    (!events?.data || !Array.isArray(events.data) || events.data.length === 0);
  const noMyFavouritesData =
    isMyFavourites &&
    volunteersEventsResolved &&
    (!volunteersEvents?.data ||
      !Array.isArray(volunteersEvents.data) ||
      volunteersEvents.data.length === 0);

  useEffect(() => {
    const queryParams: Record<string, any> =
      activeFilter === "all"
        ? { page: events?.page || 1 }
        : activeFilter === "is_starting_soon"
          ? { page: events?.page || 1, is_starting_soon: "true" }
          : { page: events?.page || 1, event_type: activeFilter };

    if (currentUser) {
      fetchEvents(queryParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    const queryParams: Record<string, any> =
      activeFilter === "all"
        ? { page: value.toString() }
        : activeFilter === "is_starting_soon"
          ? { page: value.toString(), is_starting_soon: "true" }
          : { page: value.toString(), event_type: activeFilter };

    fetchEvents(queryParams);
  };

  const error = isMyFavourites ? volunteersEventsError : eventsError;
  if (error)
    return (
      <div className={classNames(styles.eventList, styles.error)}>
        {t(error)}
      </div>
    );

  if (noDataOrg)
    return (
      <div className={classNames(styles.eventList, styles.error)}>
        {t("events.emptyOrg")}
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate("/organisation/new-event")}
        >
          {t("nav.createEvent")}
        </Button>
      </div>
    );

  return (
    <div className={styles.eventList}>
      {!isMyFavourites && (
        <div className={styles.filters}>
          {defaultFilters.map((filter) => {
            return (
              <Chip
                key={filter.id}
                color={filter.id === activeFilter ? "secondary" : "default"}
                variant={filter.id === activeFilter ? "filled" : "outlined"}
                label={filter.label}
                onClick={() => setActiveFilter(filter.id)}
              />
            );
          })}
        </div>
      )}

      <div className={styles.events}>
        {(isMyFavourites ? !volunteersEventsResolved : !eventsResolved) ? (
          <div className={styles.center}>
            <CircularProgress />
          </div>
        ) : !isMyFavourites && noFilteredDataOrg ? (
          <div className={styles.center}>{t("events.emptyFiltered")}</div>
        ) : !isMyFavourites && noDataVolunteer ? (
          <div className={styles.center}>{t("events.emptyFiltered")}</div>
        ) : isMyFavourites && noMyFavouritesData ? (
          <div className={styles.center}>{t("events.emptyMine")}</div>
        ) : (
          (() => {
            const dataToRender = isMyFavourites
              ? volunteersEvents?.data
              : events?.data;
            return (
              Array.isArray(dataToRender) &&
              dataToRender.map((event: any) => {
                // @ts-ignore - JS component
                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    isVolunteer={isVolunteer}
                  />
                );
              })
            );
          })()
        )}
      </div>
      <div className={styles.pagination}>
        <Pagination
          shape="rounded"
          color="primary"
          count={Number(
            isMyFavourites
              ? volunteersEvents?.total_pages ||
                  (Array.isArray(volunteersEvents?.data)
                    ? Math.max(1, volunteersEvents.data.length)
                    : 1)
              : events?.total_pages ||
                  (Array.isArray(events?.data)
                    ? Math.max(1, events.data.length)
                    : 1),
          )}
          page={Number(
            isMyFavourites
              ? (volunteersEvents?.page ?? 1)
              : (events?.page ?? 1),
          )}
          onChange={handlePageChange}
          renderItem={(item) => (
            <PaginationItem
              slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
              {...item}
            />
          )}
        />
      </div>
    </div>
  );
};
